import { useEffect, useMemo, useRef } from 'react';
import { Box, useMediaQuery } from '@chakra-ui/react';

export interface IDataWheelPickerSelect {
  id: string;
  name: string;
}

export interface IWheelPicker {
  items: IDataWheelPickerSelect[];
  value?: IDataWheelPickerSelect;
  onChange(v: IDataWheelPickerSelect): void;
  itemHeight?: number;
  visibleRows?: number;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

export const WheelPicker: React.FC<IWheelPicker> = ({ items, value, onChange, itemHeight = 44, visibleRows = 5 }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mediaQuery] = useMediaQuery('(max-width: 380px)');

  const containerH = itemHeight * (visibleRows % 2 === 1 ? visibleRows : visibleRows + 1);
  const pad = (containerH - itemHeight) / 2;
  const topPad = pad;
  const bottomPad = pad + 1;

  const indexOf = useMemo(() => items.findIndex((i) => i?.id === value?.id), [items, value]);

  const scrollToIndex = (idx: number, behavior: ScrollBehavior = 'auto') => {
    const el = containerRef.current;
    if (!el) return;
    const top = topPad + idx * itemHeight - (containerH - itemHeight) / 2;
    el.scrollTo({ top, behavior });
  };

  useEffect(() => {
    if (indexOf >= 0) scrollToIndex(indexOf, 'auto');
  }, [indexOf]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let t: ReturnType<typeof setTimeout>;

    const onScroll = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const center = el.scrollTop + containerH / 2;
        const raw = (center - topPad - itemHeight / 2) / itemHeight;
        const idx = clamp(Math.round(raw), 0, items.length - 1);
        const selected = items[idx];
        if (selected && value && selected.id !== value.id) {
          onChange(selected);
        }
        scrollToIndex(idx, 'smooth');
      }, 60);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      el.removeEventListener('scroll', onScroll);
    };
  }, [containerH, itemHeight, items, onChange, topPad, value]);

  useEffect(() => {
    if (!value && items.length > 0) {
      onChange(items[0]);
      scrollToIndex(0, 'auto');
    }
  }, [value, items]);

  const fontSize = mediaQuery ? 12 : 14;

  return (
    <Box position="relative" h={`${containerH}px`}>
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translateY(-50%) translateX(-50%)"
        height={itemHeight + 5}
        w="95%"
        background="#414141"
        pointerEvents="none"
        borderRadius="5px"
        zIndex={-1}
      />
      <Box
        ref={containerRef}
        role="listbox"
        aria-activedescendant={String(value)}
        tabIndex={0}
        h="100%"
        overflowY="scroll"
        overscrollBehavior="contain"
        scrollSnapType="y mandatory"
        css={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        sx={{ '&::-webkit-scrollbar': { display: 'none' } }}
        _focusVisible={{
          outline: 'none',
        }}
      >
        <Box h={`${topPad}px`} flex="none" />

        {items.map((item, i) => {
          const selected = item?.id === value?.id;

          return (
            <Box
              key={String(item.id)}
              id={String(item.id)}
              role="option"
              aria-selected={selected}
              onClick={() => {
                onChange(item);
                scrollToIndex(i, 'smooth');
              }}
              h={`${itemHeight}px`}
              display="flex"
              alignItems="center"
              justifyContent="center"
              scrollSnapAlign="center"
              userSelect="none"
              px={5}
              boxSizing="border-box"
              fontSize={`${selected ? fontSize : fontSize - 3}px`}
              fontWeight={selected ? 'bold' : 'normal'}
              color={selected ? 'white' : '#999999'}
              transition="all 0.12s ease"
              cursor="pointer"
            >
              {item && item.name.length > 80 ? `${item.name.substring(0, 75)}...` : item.name}
            </Box>
          );
        })}

        <Box h={`${bottomPad}px`} flex="none" />
      </Box>
    </Box>
  );
};
